<template>
    <app-layout>
        <template #header>
            <FarmTitle :farm="farm">
                Export ({{ new Intl.DateTimeFormat('de-DE', { dateStyle: 'short' }).format(new Date(exportObj.date)) }})
            </FarmTitle>
        </template>

        <div class="py-12">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <div class="bg-white shadow overflow-hidden sm:rounded-lg">
                    <div class="px-4 py-5 sm:px-6">
                        <h3 class="text-lg leading-6 font-medium text-gray-900">
                            Export
                        </h3>
                        <p class="mt-1 max-w-2xl text-sm text-gray-500">
                            zum {{ new Intl.DateTimeFormat('de-DE', { dateStyle: 'long' }).format(new Date(exportObj.date)) }}
                        </p>
                    </div>
                    <div class="border-t border-gray-200 px-4 py-5 sm:px-6">
                        <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                            <div class="sm:col-span-1">
                                <dt class="text-sm font-medium text-gray-500">
                                    Pferde
                                </dt>
                                <dd class="mt-1 text-sm text-gray-900">
                                    {{ exportObj.horse_count }}
                                </dd>
                            </div>
                            <div class="sm:col-span-1">
                                <dt class="text-sm font-medium text-gray-500">
                                    Kunden
                                </dt>
                                <dd class="mt-1 text-sm text-gray-900">
                                    {{ exportObj.customer_count }}
                                </dd>
                            </div>
                            <div class="sm:col-span-1">
                                <dt class="text-sm font-medium text-gray-500">
                                    Summe
                                </dt>
                                <dd class="mt-1 text-sm text-gray-900">
                                    {{ new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(exportObj.value) }}
                                </dd>
                            </div>
                            <div class="sm:col-span-1">
                                <dt class="text-sm font-medium text-gray-500">
                                    Davon abgebucht
                                </dt>
                                <dd class="mt-1 text-sm text-gray-900">
                                    {{ new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(exportObj.sepa_value) }} (=> {{ new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(exportObj.value - exportObj.sepa_value) }} überwiesen)
                                </dd>
                            </div>
                            <div class="sm:col-span-1">
                                <dt class="text-sm font-medium text-gray-500">
                                    Abbuchungstext
                                </dt>
                                <dd class="mt-1 text-sm text-gray-900">
                                    {{ exportObj.text }}
                                </dd>
                            </div>
                            <div class="sm:col-span-1">
                                <dt class="text-sm font-medium text-gray-500">
                                    Rückgäng machen
                                </dt>
                                <dd class="mt-1 text-sm text-indigo-900 hover:text-indigo-700">
                                    <a @click="revert_export()">Export rückgängig machen</a>
                                </dd>
                            </div>
                            <div class="sm:col-span-2">
                                <dt class="text-sm font-medium text-gray-500">
                                    Kunden
                                </dt>
                                <dd class="mt-1 text-sm text-gray-900 overflow-x-scroll">
                                    <table class="min-w-full divide-y divide-gray-200">
                                        <thead class="bg-gray-50">
                                        <tr>
                                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Kunde
                                            </th>
                                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Summe
                                            </th>
                                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Zahlung
                                            </th>
                                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Pferde
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody class="bg-white divide-y divide-gray-200">
                                        <tr v-for="customer in exportObj.customer_detail.sort((a, b) => {
                                            if(!a.sepa && b.sepa) {
                                                return -1;
                                            }
                                            if(!b.sepa && a.sepa) {
                                                return 1;
                                            }
                                            return 0;
                                        })" :key="customer.id">
                                            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {{ customer.details.name }} ({{ customer.details.kdnr }})
                                            </td>
                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {{ new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(customer.value) }}
                                            </td>
                                            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                <template v-if="!customer.sepa?.sepa_details">
                                                    <span v-if="customer.sepa?.transferred" class="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                                                        Überwiesen
                                                    </span>
                                                    <template v-else>
                                                        <span class="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                                                            Wird überwiesen
                                                        </span>
                                                        <inertia-link :href="route('export.reportTransfer', { export: exportObj, customer: customer.details })" class="text-primary-500 hover:text-primary-900 hover:underline">
                                                            Als überwiesen melden
                                                        </inertia-link>
                                                    </template>
                                                </template>
                                                <a v-else :href="'https://pferde.markuswipperfürth.de/' + customer.sepa.token + '/' + customer.sepa.sepa_details.iban_censored.replaceAll('+', '').replaceAll(' ', '').substr(customer.sepa.sepa_details.iban_censored.length -4)" class="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                                    Abgebucht ({{ customer.sepa.sepa_details.iban_censored }})
                                                </a>
                                            </td>
                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {{ new Intl.NumberFormat('de-DE', { style: 'decimal' }).format(customer.horses.length) }}
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </div>
        </div>
    </app-layout>
</template>

<script>
    import AppLayout from "@/Layouts/AppLayout";
    import FarmTitle from "@/Layouts/FarmTitle";
    import axios from "axios";
    export default {
        name: "Detail",
        components: {FarmTitle, AppLayout},
        props: ['farm', 'exportObj', 'start_date'],
        methods: {
            revert_export: function() {
                console.log("reverting export");
                if(confirm("Soll der Export wirklich rückgängig gemacht werden?")) {
                    axios.post(this.route('export.revert', { export: this.exportObj }));
                }
            }
        }
    }
</script>

<style scoped>

</style>
